'use client';

import { useState } from 'react';
import StatsCard from "@/components/yayasan/dashboard/StatsCard";
import SchoolCard from "@/components/yayasan/dashboard/SchoolCard";
import FinancialChart from "@/components/yayasan/dashboard/FinancialChart";
import BudgetOverview from "@/components/yayasan/dashboard/BudgetOverview";
import TransactionList from "@/components/yayasan/dashboard/TransactionList";
import Link from 'next/link';
import { ExternalLink } from 'lucide-react';
import { yayasanStats, schoolsByPeriod } from '@/lib/data';

type Period = 'daily' | 'weekly' | 'monthly' | 'yearly';

export default function YayasanDashboard() {
  const [selectedPeriod, setSelectedPeriod] = useState<Period>('monthly');

  const periodLabels: Record<Period, string> = {
    daily: 'Hari',
    weekly: 'Minggu',
    monthly: 'Bulan',
    yearly: 'Tahun',
  };

  const periods: Period[] = ['daily', 'weekly', 'monthly', 'yearly'];
  const currentSchools = schoolsByPeriod[selectedPeriod];
  const unitResponsive = 'bg-red-800 sm:bg-amber-700 md:bg-yellow-600 lg:bg-orange-500 xl:bg-red-800 2xl:bg-black/2'

  return (
    <main className={`px-4 py-8 md:p-10 space-y-6 md:space-y-10 max-w-7xl h-fit mx-auto w-full pb-24 ${unitResponsive}`}>
      <section className="flex flex-col md:flex-row justify-between items-center bg-surface p-8 rounded-4xl border border-border-main shadow-sm relative overflow-hidden group hover:shadow-md transition-all duration-300">
        {/* Background Decorations */}
        <div className="absolute top-0 right-0 w-64 h-64 bg-primary-soft/40 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2 pointer-events-none opacity-0 group-hover:opacity-100 transition-opacity duration-700"></div>
        <div className="absolute bottom-0 left-0 w-32 h-32 bg-secondary/5 rounded-full blur-2xl translate-y-1/2 -translate-x-1/2 pointer-events-none"></div>

        <div className="relative z-10 w-full md:w-auto">
          <div className="inline-flex items-center gap-2 mb-3 bg-primary-soft/50 px-3 py-1.5 rounded-full border border-primary/10 hover:bg-primary-soft transition-colors cursor-default">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary opacity-75"></span>
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-primary"></span>
            </span>
            <span className="text-[10px] font-bold text-primary uppercase tracking-widest">Sistem Keuangan Terpadu</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight mb-2">
            Financial <span className="text-transparent bg-clip-text bg-linear-to-r from-primary to-primary-light">Overview</span>
          </h2>
          <p className="text-secondary font-medium max-w-xl leading-relaxed text-sm md:text-base">
            Monitor performa finansial seluruh unit pendidikan Yayasan secara holistik dengan data real-time.
          </p>
        </div>

        <div className="flex flex-wrap gap-4 relative z-10 mt-6 md:mt-0 w-full md:w-auto justify-start md:justify-end">
          <button className="px-6 py-3.5 bg-surface border border-border-main rounded-2xl text-xs font-bold text-foreground hover:bg-primary-soft/50 hover:border-primary/30 transition-all shadow-sm hover:shadow-md flex items-center gap-3 group uppercase tracking-widest">
            <span className="bg-border-main/50 p-1 rounded-md group-hover:bg-primary group-hover:text-white transition-colors duration-300">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" x2="12" y1="15" y2="3" /></svg>
            </span>
            <span>Unduh Laporan</span>
          </button>
          <button className="px-6 py-3.5 gradient-green text-white rounded-2xl text-xs font-bold hover:shadow-lg hover:shadow-primary/25 hover:-translate-y-0.5 transition-all flex items-center gap-3 uppercase tracking-widest group">
            <span className="bg-white/20 p-1 rounded-md group-hover:bg-white/30 transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><line x1="12" x2="12" y1="5" y2="19" /><line x1="5" x2="19" y1="12" y2="12" /></svg>
            </span>
            <span>Transaksi Baru</span>
          </button>
        </div>
      </section>

      <section className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 xl:grid-cols-4 gap-6">
        {yayasanStats.map((stat) => (
          <StatsCard key={stat.label} {...stat} />
        ))}
      </section>

      <div className="grid grid-cols-1 h-fit lg:grid-cols-3 gap-10">
        <section className="lg:col-span-3 space-y-8">
          <div className="flex items-center justify-center px-2 sm:justify-between">
            <div className="hidden sm:block items-end gap-4">
              <div className="">
                <h3 className="text-2xl font-bold text-foreground">Integrasi Unit</h3>
                <p className="text-xs text-secondary font-medium tracking-wide">Pilih unit untuk melihat rincian riwayat data dana</p>
              </div>
            </div>
            <div className="flex bg-surface p-1.5 rounded-2xl border border-border-main shadow-sm">
              {periods.map((period) => (
                <button
                  key={period}
                  onClick={() => setSelectedPeriod(period)}
                  className={`px-5 py-2 rounded-xl text-[10px] font-bold uppercase tracking-widest transition-all ${selectedPeriod === period
                    ? 'bg-primary text-white shadow-md'
                    : 'text-muted hover:text-foreground'
                    }`}
                >
                  {periodLabels[period]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid  grid-cols-1 sm:grid-cols-2 gap-5">
            {currentSchools.map((school) => (
              <SchoolCard key={school.id} {...school} />
            ))}
          </div>
          <FinancialChart />
        </section>
      </div>
      <aside className="space-y-10 h-fit gap-0 grid grid-cols-1 md:grid-cols-2 md:gap-6 w-full">



      </aside>
    </main>
  );
}




